import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * 认证守卫Hook - 检查登录状态并提供登录窗口控制
 * @returns {Object} 包含登录检查函数和登录窗口控制状态
 */
export const useAuthGuard = () => {
  const { isLoggedIn } = useSelector((state) => state.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const pendingCallbackRef = useRef(null);

  /**
   * 检查用户是否已登录，如果未登录则显示登录窗口
   * @param {Function} onSuccess - 登录成功后的回调函数（可选）
   * @returns {boolean} 是否已登录
   */
  const requireAuth = useCallback((onSuccess) => {
    if (isLoggedIn) {
      // 已登录，直接返回true，不执行回调（避免无限循环）
      return true;
    } else {
      // 未登录，保存回调并显示登录窗口
      if (onSuccess && typeof onSuccess === 'function') {
        pendingCallbackRef.current = onSuccess;
      }
      setShowLoginModal(true);
      return false;
    }
  }, [isLoggedIn]);

  /**
   * 关闭登录窗口
   */
  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  /**
   * 登录成功后的处理
   */
  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false);
    // 执行之前保存的回调函数
    if (pendingCallbackRef.current) {
      const callback = pendingCallbackRef.current;
      pendingCallbackRef.current = null; // 清除回调避免重复执行
      setTimeout(callback, 100); // 延迟执行，确保状态更新完成
    }
  }, []);

  return {
    isLoggedIn,
    showLoginModal,
    requireAuth,
    closeLoginModal,
    handleLoginSuccess
  };
};